import {
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDocumentTitle,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { apiFetch } from "../../api";
import type { Project, ProjectDetailResponse } from "../../configTypes";
import {
  buildPipelineGraph,
  type BuildPipelineGraphInput,
  type PipelineGraph,
} from "../../lib/buildPipelineGraph";
import { ProjectStatusBadge } from "../config/ProjectDetails";
import { PipelineDag, type TableSelection } from "./PipelineDag";
import { PipelineDrawer } from "./PipelineDrawer";

function useActiveProjects(): Project[] | null {
  const [projects, setProjects] = useState<Project[] | null>(null);
  useEffect(() => {
    void apiFetch<Project[]>("/api/projects")
      .then((all) => setProjects(all.filter((p) => p.archivedAt == null)))
      .catch(() => setProjects([])); // switcher is non-critical; degrade silently
  }, []);
  return projects;
}

function ProjectSwitcher({ currentProjectId }: { currentProjectId: string }) {
  const navigate = useNavigate();
  const projects = useActiveProjects();
  if (projects == null || projects.length <= 1) return null;
  return (
    <Select
      value={currentProjectId}
      onValueChange={(id) => {
        if (id !== currentProjectId) void navigate(`/pipeline/${id}`);
      }}
    >
      <SelectTrigger className="w-64" aria-label="Switch project">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {projects.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            {p.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Derived, not re-declared. A local shape listing only `datasets` made `project.reportedAt` — sent
 * by the endpoint, and what staleness is measured against (SIM-2576) — invisible to the compiler,
 * reaching buildPipelineGraph by structural luck. Re-declaring a wire type is the SIM-2733 class.
 */
type SnapshotResponse = BuildPipelineGraphInput["snapshot"];

/**
 * SIM-2845: the DAG's verdicts come from run records, so this page reads `run-status` alongside the
 * snapshot. Its failure is deliberately tolerated rather than fatal — `runStatus: null` renders
 * every node `unknown`, which is honest, whereas failing the page would also lose the graph
 * structure that does not depend on it. Snapshot and detail stay fatal: no graph without.
 */
type RunStatusResponse = NonNullable<BuildPipelineGraphInput["runStatus"]>;

type PipelineFetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      snapshot: SnapshotResponse;
      projectDetail: ProjectDetailResponse;
      runStatus: RunStatusResponse | null;
    };

function ProjectPicker() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<Project[]>("/api/projects")
      .then((all) => setProjects(all.filter((p) => p.archivedAt == null)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="empty-state">Loading projects...</div>;
  if (error) return <div className="empty-state text-destructive">{error}</div>;

  if (projects.length === 0) {
    return <EmptyState title="No active projects" />;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <h2 className="mb-4 text-2xl text-foreground">Pipeline</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Choose a project to view its data pipeline as a DAG.
      </p>
      <ul className="admin-table-container">
        {projects.map((p) => (
          <li key={p.id} className="border-b border-border last:border-0">
            <Link
              to={`/pipeline/${p.id}`}
              className="block px-4 py-3 hover:bg-accent focus:bg-accent focus:outline-none"
            >
              <span className="inline-flex items-center gap-2 text-foreground">
                {p.name}
                <ProjectStatusBadge status={p.status} />
              </span>
              <span className="ml-3 text-xs text-muted-foreground">{p.id}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PipelineForProject({ projectId }: { projectId: string }) {
  // The component is keyed on `projectId` by the page wrapper so it remounts
  // (and resets `state` to "loading") whenever the URL param changes. That
  // avoids synchronous setState resets in the effect body.
  const [state, setState] = useState<PipelineFetchState>({ status: "loading" });
  // SIM-1350 §6: single-click → focus (drives cascade highlight); double-click
  // → drawer. The two pieces of state are independent so a user can focus a
  // node first, then drill into details on the same or a different node.
  const [drawerSelection, setDrawerSelection] = useState<TableSelection | null>(null);
  const [focusedSelection, setFocusedSelection] = useState<TableSelection | null>(null);

  const projectName = state.status === "ready" ? state.projectDetail.project.name : null;
  useDocumentTitle(projectName ? `Pipeline · ${projectName}` : null, {
    suffix: " | Simplicity Admin",
  });

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      apiFetch<SnapshotResponse>(`/api/admin/snapshot/${projectId}`),
      apiFetch<ProjectDetailResponse>(`/api/projects/${projectId}/detail`),
      apiFetch<RunStatusResponse>(`/api/admin/run-status/${projectId}`).catch(() => null),
    ])
      .then(([snapshot, projectDetail, runStatus]) => {
        if (cancelled) return;
        setState({ status: "ready", snapshot, projectDetail, runStatus });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setState({ status: "error", message: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (state.status === "loading") return <div className="empty-state">Loading pipeline...</div>;
  if (state.status === "error") {
    return <div className="empty-state text-destructive">{state.message}</div>;
  }

  const graph: PipelineGraph = buildPipelineGraph({
    snapshot: state.snapshot,
    projectDetail: state.projectDetail,
    runStatus: state.runStatus,
    now: new Date(),
  });

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl text-foreground">{state.projectDetail.project.name}</h2>
          <p className="text-sm text-muted-foreground">Pipeline DAG</p>
        </div>
        <ProjectSwitcher currentProjectId={projectId} />
      </header>
      {graph.visibleWaves.length === 0 ? (
        <EmptyState title="No pipeline data" description="This project has no waves to render." />
      ) : (
        <PipelineDag
          graph={graph}
          onTableSelect={setDrawerSelection}
          focusedSelection={focusedSelection}
          onFocusChange={setFocusedSelection}
        />
      )}
      <PipelineDrawer
        selection={drawerSelection}
        snapshot={state.snapshot}
        projectDetail={state.projectDetail}
        onClose={() => setDrawerSelection(null)}
      />
    </div>
  );
}

export function PipelinePage() {
  const { projectId } = useParams<{ projectId: string }>();
  if (projectId == null) return <ProjectPicker />;
  return <PipelineForProject key={projectId} projectId={projectId} />;
}
